import { timestampDate } from '@bufbuild/protobuf/wkt';
import { useListAuditEvents } from '@sunstone/api-client/hooks';
import { Badge, Card, CardAction, CardContent, CardHeader, CardTitle } from '@sunstone/ui';
import { Rocket } from 'lucide-react';
import { Link } from 'react-router';
import { useAuthToken } from '@/features/auth';
import { relativeTime } from '@/lib/time';

const FEED_PAGE_SIZE = 15;
const REFETCH_MS = 30_000;

// Tailwind class pairs per release action verb — dot class + badge class
const VERB_CLASSES: Record<string, { dot: string; badge: string }> = {
  approved: {
    dot: 'bg-[#00D4A4]',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  },
  rolled_back: {
    dot: 'bg-rose-500',
    badge: 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300',
  },
  deprecated: {
    dot: 'bg-slate-400',
    badge: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  },
  notified: {
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300',
  },
  validation_status_reported: {
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  },
};
const DEFAULT_VERB_CLASSES = {
  dot: 'bg-slate-600',
  badge: 'bg-muted text-muted-foreground',
};

// "release.validation_status_reported" → "validation_status_reported"
function actionVerb(eventType: string): string {
  return eventType.split('.').pop() || eventType;
}

// "validation_status_reported" → "Validation status reported"; returns '' for empty input
function actionLabel(verb: string): string {
  if (!verb) return '';
  return verb.charAt(0).toUpperCase() + verb.slice(1).replace(/_/g, ' ');
}

export function ReleaseEvents() {
  const { token } = useAuthToken();
  const { data, isLoading } = useListAuditEvents(
    token,
    'release',
    undefined,
    REFETCH_MS,
    FEED_PAGE_SIZE,
  );

  const events = data?.events ?? [];

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3 flex-shrink-0">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Rocket className="h-4 w-4 text-muted-foreground" />
          Release events
        </CardTitle>
        <CardAction>
          <Link
            to="/releases"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary underline underline-offset-4"
          >
            View all releases
          </Link>
        </CardAction>
      </CardHeader>
      <CardContent className="pt-0 flex-1 overflow-y-auto max-h-[380px]">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-9 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground">No release events yet.</p>
        ) : (
          <ol className="divide-y divide-border">
            {events.map((event) => {
              const createdAt = event.createdAt ? timestampDate(event.createdAt) : null;
              const verb = actionVerb(event.eventType);
              const verbClasses = VERB_CLASSES[verb] ?? DEFAULT_VERB_CLASSES;
              const action = actionLabel(verb);
              const detail = event.description || action || '—';

              return (
                <li key={event.id} className="py-2.5 flex items-center gap-3">
                  {/* Action verb dot */}
                  <span className={`h-2 w-2 rounded-full flex-shrink-0 ${verbClasses.dot}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-medium truncate leading-snug">{detail}</p>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0">
                        {createdAt ? relativeTime(createdAt) : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {action && (
                        <Badge className={`text-[10px] px-1.5 py-px h-auto ${verbClasses.badge}`}>
                          {action}
                        </Badge>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
